/**
 * PublicInstallAppBanner — App-Werbe-Banner für NICHT eingeloggte Kunden,
 * die das Portal über einen WhatsApp/Email-Link öffnen.
 *
 * Wird auf der Login-Seite (CustomerPortal.jsx) eingehängt, weil
 * `MobilePromoBanner.jsx` einen Auth-Token braucht und damit erst NACH
 * Login angezeigt wird.
 *
 * Verhalten:
 *  - Lädt die aktive Mobile-Promo-Konfiguration vom Public-Endpoint
 *    `/api/portal/mobile-promo/public` (ohne Auth).
 *  - Zeigt nur auf Mobile-UAs (iOS/Android) — auf Desktop blendet
 *    der grosse Login-MobilePromoBanner mit QR-Code ein.
 *  - Dismiss-Status wird in `localStorage` gespeichert
 *    (Schlüssel `sf_install_banner_dismissed_v1`).
 *  - Tracking-Events landen weiter auf demselben `mobile-promo/event`,
 *    aber ohne `campaign_id` (anonyme View).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Smartphone, X, Apple, PlayCircle } from 'lucide-react';

const STORAGE_KEY = 'sf_install_banner_dismissed_v1';
const DISMISS_HOURS = 24 * 7; // eine Woche

const isIOS = () => /iPhone|iPad|iPod/i.test(window.navigator.userAgent);
const isAndroid = () => /Android/i.test(window.navigator.userAgent);
const isMobile = () => isIOS() || isAndroid();

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const PublicInstallAppBanner = () => {
  const { t } = useTranslation();
  const [promo, setPromo] = useState(null);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!isMobile()) return;
    // Dismiss-Check
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (!Number.isNaN(ts) && Date.now() - ts < DISMISS_HOURS * 3600 * 1000) {
          setVisible(false);
          return;
        }
      }
    } catch (_e) {
      // ignore
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/portal/mobile-promo/public`);
        if (!cancelled && res.data?.active) {
          setPromo(res.data);
        }
      } catch (_e) {
        // ohne Banner stillschweigend weiter
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (_e) {
      // ignore
    }
    setVisible(false);
  }, []);

  const handleClick = useCallback((store) => {
    const url = store === 'ios' ? promo?.ios_url : promo?.android_url;
    if (!url) return;
    // Best-effort tracking (anonym, ohne Auth)
    axios.post(`${API}/portal/mobile-promo/public/event`, {
      type: `click_${store}`,
      campaign_id: promo?.campaign_id || null,
      anonymous: true,
    }).catch(() => {});
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [promo]);

  if (!visible || !promo || !isMobile()) return null;

  const targetUrl = isIOS() ? promo.ios_url : promo.android_url;
  const storeLabel = isIOS() ? 'App Store' : 'Play Store';
  const StoreIcon = isIOS() ? Apple : PlayCircle;

  if (!targetUrl) return null;

  return (
    <div
      data-testid="public-install-app-banner"
      className="rounded-xl p-4 flex items-center gap-3 shadow-lg"
      style={{
        background: 'linear-gradient(135deg, #141310 0%, #2a2a1a 100%)',
        border: '1px solid #c9a34e',
      }}
    >
      <div
        className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
        style={{ backgroundColor: '#c9a34e' }}
      >
        <Smartphone className="w-5 h-5 text-black" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold text-sm">
          {promo.title || t('portal.banner.install_title')}
        </div>
        <div className="text-gray-300 text-xs mt-0.5 truncate">
          {promo.subtitle || t('portal.banner.install_subtitle')}
        </div>
      </div>
      <button
        type="button"
        onClick={() => handleClick(isIOS() ? 'ios' : 'android')}
        data-testid="public-install-app-banner-cta"
        className="flex-shrink-0 px-3 py-2 rounded-lg flex items-center gap-1.5 text-xs font-semibold transition-transform active:scale-95"
        style={{ backgroundColor: '#c9a34e', color: '#000' }}
      >
        <StoreIcon className="w-4 h-4" />
        {storeLabel}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="public-install-app-banner-dismiss"
        aria-label={t('portal.banner.dismiss')}
        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
        style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
      >
        <X className="w-3.5 h-3.5 text-gray-400" />
      </button>
    </div>
  );
};

export default PublicInstallAppBanner;
